import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  HideWithCSS,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  Spinner,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
} from "@biblioteksentralen/react";
import { getTypedEntries } from "@libry-content/types";
import { ReactNode, useState } from "react";
import { ChevronDown, Sliders, X } from "react-feather";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { DefaultContainer } from "../ContentContainer";
import { useEventsListContext } from "./EventFilterContext";
import { EventListFilter, useResolvedEventFacets } from "./facetUtils";
import { EventFacetName } from "@libry-content/common";
import { CalendarInput } from "./CalendarInput";
import { sift } from "radash";
import { FrontendLocale } from "@libry-content/localization";
import { localizedFormat } from "../../utils/date";

export const EventFacetButtons = () => {
  const { toggleFacet, isValidating, urlState, setDateFilter, finishedOrUnfinishedEvents } = useEventsListContext();
  const resolvedFacets = useResolvedEventFacets();
  const { t, lang } = useTranslation();
  const [filterMobileOpen, setFilterMobileOpen] = useState(false);
  const facetEntries = resolvedFacets ? getTypedEntries(resolvedFacets) : [];
  const fromDate = urlState?.from;
  const toDate = urlState?.to;

  const currentFacetFilters =
    facetEntries.flatMap(([facetName, facet]) => facet.currentFilters.map((filter) => ({ filter, facetName }))) ?? [];

  const dateRange =
    finishedOrUnfinishedEvents === "unfinished"
      ? {
          start: new Date(),
        }
      : {
          end: new Date(),
        };

  const relevantDateFilter = finishedOrUnfinishedEvents === "unfinished" ? "from" : "to";
  const onChangeDate = (date: string | undefined) => relevantDateFilter && setDateFilter(relevantDateFilter, date);

  const allCurrentFilters = sift([...currentFacetFilters, fromDate, toDate]);

  if (!resolvedFacets) return null;

  return (
    <>
      <HideWithCSS below="sm" marginBottom="0.5rem">
        <HStack>
          {facetEntries.map(
            ([facetName, facet]) =>
              facet.availableFilters.length > 1 && (
                <FilterPopover key={facetName} label={facet.label} placement="bottom-start">
                  <FilterOptions facetName={facetName} filters={facet.availableFilters} />
                </FilterPopover>
              )
          )}
          <FilterPopover label={t("Dato")} placement="bottom-end">
            <CalendarInput value={urlState?.from} onChange={onChangeDate} dateRange={dateRange} />
          </FilterPopover>
          {isValidating && <Spinner />}
        </HStack>
      </HideWithCSS>
      <HideWithCSS above="sm">
        <DefaultContainer paddingInlineStart="0">
          <Button
            isLoading={isValidating}
            variant="outline"
            leftIcon={filterMobileOpen ? <X /> : <Sliders style={{ transform: "rotate(90deg)" }} size="1em" />}
            marginBottom="0.5rem"
            // TODO: Should closing the filter on mobile also remove the filters?
            onClick={() => setFilterMobileOpen(!filterMobileOpen)}
          >
            {t("Filter")}
          </Button>
        </DefaultContainer>
        {filterMobileOpen && (
          <Accordion paddingTop="1rem" paddingBottom="1rem" allowMultiple allowToggle>
            {facetEntries.map(
              ([facetName, facet]) =>
                facet.availableFilters.length > 1 && (
                  <FilterAccordion key={facetName} label={facet.label}>
                    <FilterOptions facetName={facetName} filters={facet.availableFilters} />
                  </FilterAccordion>
                )
            )}
            <FilterAccordion label={t("Dato")}>
              <Box display="flex" justifyContent="center" padding="0" paddingBottom="1rem">
                <CalendarInput value={urlState?.from} onChange={onChangeDate} dateRange={dateRange} hideWeekNumber />
              </Box>
            </FilterAccordion>
          </Accordion>
        )}
      </HideWithCSS>
      <Stack
        direction="row"
        paddingBottom={allCurrentFilters.length ? 4 : 0}
        gridGap={2}
        wrap="wrap"
        justifyContent="flex-start"
      >
        {fromDate && (
          <FilterTag
            filter={{ label: t("Fra {dato}", { dato: getDateString(fromDate, lang) }), value: fromDate }}
            onClick={() => setDateFilter("from", undefined)}
          />
        )}
        {toDate && (
          <FilterTag
            filter={{ label: t("Til {dato}", { dato: getDateString(toDate, lang) }), value: toDate }}
            onClick={() => setDateFilter("to", undefined)}
          />
        )}
        {currentFacetFilters.map(({ facetName, filter }) => (
          <FilterTag key={filter.value} filter={filter} onClick={() => toggleFacet(facetName, filter.value)} />
        ))}
      </Stack>
    </>
  );
};

const getDateString = (date: string, lang: FrontendLocale) => localizedFormat(date, "PPP", lang);

const FilterTag = ({ filter, onClick }: { filter: EventListFilter; onClick: () => void }) => {
  return (
    <Tag size="md" borderRadius="full" variant="solid" bg="#000">
      <TagLabel>{filter.label}</TagLabel>
      <TagCloseButton
        id={filter.value}
        name={filter.label}
        onClick={onClick}
        opacity="1"
        _focus={{ border: ".15rem solid #fff" }}
      />
    </Tag>
  );
};

const FilterPopover = ({ label, children, ...rest }: { label: string; children: ReactNode } & PopoverProps) => {
  return (
    <Popover {...rest}>
      <PopoverTrigger>
        <Button size="md" variant="outline" rightIcon={<ChevronDown />}>
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent width="auto">
        <PopoverBody>{children}</PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

const FilterAccordion = ({ label, children }: { label: string; children: ReactNode }) => {
  return (
    <AccordionItem>
      <h2>
        <AccordionButton>
          <Box flex="1" textAlign="left">
            <Text fontWeight={600}>{label}</Text>
          </Box>
          <AccordionIcon />
        </AccordionButton>
      </h2>
      <AccordionPanel paddingBottom="1rem">{children}</AccordionPanel>
    </AccordionItem>
  );
};

const FilterOptions = (props: { facetName: EventFacetName; filters: EventListFilter[] }) => {
  const { toggleFacet } = useEventsListContext();
  const resolvedFacets = useResolvedEventFacets();

  return (
    <Stack direction="column">
      {props.filters.map(({ label, value }) => {
        const isChecked = !!resolvedFacets?.[props.facetName]?.currentFilters.find((filter) => filter.value === value);
        return (
          <Checkbox key={value} onChange={() => toggleFacet(props.facetName, value)} isChecked={isChecked}>
            <Text fontSize=".9rem">{label}</Text>
          </Checkbox>
        );
      })}
    </Stack>
  );
};
